import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { Linking } from 'react-native'
import { createSelector } from 'reselect'

// Services
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { logger } from 'APP/Lib/Datadog'

// Actions
import LoginActions from 'APP/Redux/LoginRedux'
import AccountLinkingActions from 'APP/Redux/AccountLinkingRedux'
import { RootState } from 'APP/Store/CreateStore'

// Components
import Alert from 'APP/Converse/Alert'
import { AccountLinkingLoadingScreen } from './AccountLinkingLoadingScreen'
import { SubtitleWithEmail } from './SubtitleWithEmail'
import AccountLinkingScreenLayout, { AccountLinkingLayoutProps } from './AccountLinkingScreenLayout'

function getScreenFromSSOLinkStatus(status: string) {
  switch (status) {
    case 'force-link':
      return 'matchingEmailLogin'
    case 'suggest-link':
      // multipass matched on first name + last name + DOB,
      // or on claimed eligibility email (fallback)
      return 'fuzzyMatched'
    case 'ask-link':
      return 'nonMatchingEmail'
    default:
      return 'linkingError'
  }
}

// TODO: consider using our standard createSelector instead of using reselect
const selectAccountLinkingData = createSelector(
  (state: RootState) => state.login.customClaims,
  (state: RootState) => state.login.fetching,
  (state: RootState) => state.accountLinking.running,
  (state: RootState) => state.accountLinking.error,
  (state: RootState) => state.accountLinking.errorType,
  (customClaims, fetching, running, error, errorType) => ({
    login: { customClaims, fetching },
    accountLinking: { running, error, errorType },
  })
)

const AccountLinkingScreen = () => {
  const [screenType, setScreenType] = useState('generalError')
  const dispatch = useDispatch()
  const {
    login: { customClaims, fetching },
    accountLinking: { running, error, errorType },
  } = useSelector(selectAccountLinkingData)
  const {
    email,
    sso_link_status: ssoLinkStatus,
    sso_link_email_matches: ssoLinkMatches,
  } = customClaims

  useEffect(() => {
    if (error) {
      setScreenType('linkingError')
      return
    }

    if (!(running || fetching)) {
      setScreenType(getScreenFromSSOLinkStatus(ssoLinkStatus!))
    }
  }, [ssoLinkStatus, running, error, fetching])

  useEffect(() => {
    const analyticsConfig: Record<string, string> = {
      matchingEmailLogin: 'SSOAccountMatch',
      nonMatchingEmail: 'SSONoAccountMatch',
      fuzzyMatched: 'SSOFuzzyAccountMatch',
      nonMatchingEmailLogin: 'SSOAccountLinking',
      linkingError: 'SSOAccountLinkingError',
    }
    const screenName = analyticsConfig[screenType]

    if (screenName) {
      Analytics.trackScreen(screenName)
    }
  }, [screenType])

  const showLoading = useMemo(
    () => !error && (!ssoLinkStatus || running || fetching),
    [error, ssoLinkStatus, running, fetching]
  )

  const openSupportEmail = () => {
    const url = UrlBuilder.supportEmailUrl()

    Linking.openURL(url).catch((e) => {
      logger.error('[AccountLinkingScreen] Error opening support email link', { url }, e)

      Alert.alert(I18n.t('CannotOpenEmail.title'), I18n.t('CannotOpenEmail.body') || undefined, [
        { text: I18n.t('CannotOpenEmail.ok') || undefined },
      ])
    })
  }

  const requestAccountLinkLogin = (loginHint?: string) => {
    dispatch(LoginActions.accountLinkLoginRequest(loginHint))
  }

  const requestAccountLinking = (accountCreation = false) => {
    dispatch(AccountLinkingActions.requestAccountLinking(accountCreation))
  }

  const requestLogoutWithAccountLinkingReset = () => {
    dispatch(AccountLinkingActions.resetAccountLinking())
    dispatch(LoginActions.logout('Failed account linking', true))
  }

  const config: Record<string, () => AccountLinkingLayoutProps> = {
    matchingEmailLogin: () => ({
      title: I18n.t('AccountLinkingScreen.matchingEmailLogin.title'),
      subtitle: (
        <SubtitleWithEmail
          i18nKey="AccountLinkingScreen.matchingEmailLogin.subtitle"
          email={email!}
        />
      ),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.matchingEmailLogin.primaryButton'),
        analyticsName: 'AccountLinkingScreen.matchingEmailLogin.login',
        onPress: () => requestAccountLinkLogin(email!),
      },
      hintText: I18n.t('AccountLinkingScreen.matchingEmailLogin.hintText'),
      hintCtaConfig: {
        text: I18n.t('AccountLinkingScreen.matchingEmailLogin.hintCta'),
        onPress: openSupportEmail,
      },
    }),
    fuzzyMatched: () => ({
      title: I18n.t('AccountLinkingScreen.fuzzyMatched.title'),
      subtitle: (
        <SubtitleWithEmail
          i18nKey="AccountLinkingScreen.fuzzyMatched.subtitle"
          email={ssoLinkMatches![0]}
        />
      ),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.fuzzyMatched.primaryButton'),
        analyticsName: 'AccountLinkingScreen.fuzzyMatched.yes',
        onPress: () => requestAccountLinkLogin(ssoLinkMatches![0]),
      },
      hintText: I18n.t('AccountLinkingScreen.fuzzyMatched.hintText'),
      hintCtaConfig: {
        text: I18n.t('AccountLinkingScreen.fuzzyMatched.hintCta'),
        onPress: openSupportEmail,
      },
    }),
    nonMatchingEmail: () => ({
      title: I18n.t('AccountLinkingScreen.nonMatchingEmail.title'),
      subtitle: I18n.t('AccountLinkingScreen.nonMatchingEmail.subtitle'),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.nonMatchingEmail.primaryButton'),
        analyticsName: 'AccountLinkingScreen.nonMatchingEmail.yes',
        onPress: () => setScreenType('nonMatchingEmailLogin'),
      },
      secondaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.nonMatchingEmail.secondaryButton'),
        analyticsName: 'AccountLinkingScreen.nonMatchingEmail.no',
        onPress: () => requestAccountLinking(true),
      },
    }),
    nonMatchingEmailLogin: () => ({
      title: I18n.t('AccountLinkingScreen.nonMatchingEmailLogin.title'),
      subtitle: (
        <SubtitleWithEmail
          i18nKey="AccountLinkingScreen.nonMatchingEmailLogin.subtitle"
          email={email!}
        />
      ),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.nonMatchingEmailLogin.primaryButton'),
        analyticsName: 'AccountLinkingScreen.nonMatchingEmailLogin.login',
        onPress: () => requestAccountLinkLogin(),
      },
      onBackButtonPress: () => setScreenType('nonMatchingEmail'),
    }),
    linkingError: () => ({
      title: I18n.t('AccountLinkingScreen.linkingError.title'),
      subtitle: I18n.t('AccountLinkingScreen.linkingError.subtitle'),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.linkingError.primaryButton'),
        analyticsName: 'AccountLinkingScreen.linkingError.tryAgain',
        onPress:
          errorType === 'linking'
            ? () => requestAccountLinking()
            : () => requestAccountLinking(true),
      },
      tertiaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.linkingError.tertiaryButton'),
        analyticsName: 'AccountLinkingScreen.linkingError.takeMeBackHome',
        onPress: requestLogoutWithAccountLinkingReset,
      },
    }),
    generalError: () => ({
      title: I18n.t('AccountLinkingScreen.generalError.title'),
      subtitle: I18n.t('AccountLinkingScreen.generalError.subtitle'),
      primaryButtonConfig: {
        title: I18n.t('AccountLinkingScreen.generalError.primaryButton'),
        analyticsName: 'AccountLinkingScreen.generalError.takeMeBackHome',
        onPress: requestLogoutWithAccountLinkingReset,
      },
    }),
  }

  // When the user logs into their dia account, the access token no longer has the sso custom claim defined
  // Since we haven't reached startupNav again yet, we need to handle ssoLinkStatus=undefined in this container
  if (showLoading) return <AccountLinkingLoadingScreen />

  // Using encapsulation to defer execution of applicable sub-screen
  const screenData = config[screenType as keyof typeof config]()

  return <AccountLinkingScreenLayout {...screenData} />
}

export default AccountLinkingScreen
